import { createColumnHelper } from '@tanstack/react-table';

import { SingleUserTeamSelector } from '@/react/portainer/policies/components/SingleUserTeamSelector';

import { PortainerSelect, Option } from '@@/form-components/PortainerSelect';
import { actionsColumn } from '@@/datatables/editable/actionsColumn';
import { isEditableTableMeta } from '@@/datatables/editable/isEditableTableMeta';
import { NEW_ROW_ID } from '@@/datatables/editable/EditableDatatable';

import type { UnifiedAccess } from './types';

export type UserTeamValue = Pick<UnifiedAccess, 'Type' | 'Name' | 'PartyId'>;

interface GetColumnsDeps {
  accessList: UnifiedAccess[];
  onChange: (value: UnifiedAccess[]) => void;
  onRemove: (item: UnifiedAccess) => void;
  userOptions: Option<number>[];
  teamOptions: Option<number>[];
  roleOptions: Option<number>[];
}

export function getColumns({
  accessList,
  onChange,
  onRemove,
  userOptions,
  teamOptions,
  roleOptions,
}: GetColumnsDeps) {
  const columnHelper = createColumnHelper<UnifiedAccess>();

  // Combine users and teams into one list with proper structure
  const usersAndTeamsOptions = [
    ...userOptions.map((u) => ({
      label: u.label,
      value: { Type: 'user' as const, Name: u.label, PartyId: u.value },
    })),
    ...teamOptions.map((t) => ({
      label: t.label,
      value: { Type: 'team' as const, Name: t.label, PartyId: t.value },
    })),
  ];

  return [
    columnHelper.accessor('Name', {
      header: 'Name',
      cell: ({ row: { original, index }, table }) => {
        if (!isEditableTableMeta(table.options.meta)) {
          return null;
        }

        const editableRowIndex = table.options.meta.getEditableRow();
        if (index === editableRowIndex || original.Id === NEW_ROW_ID) {
          return (
            <SingleUserTeamSelector<UserTeamValue>
              options={usersAndTeamsOptions}
              value={original}
              onChange={(value) =>
                handleUsersAndTeamsChange(original, value || null)
              }
              data-cy="cy-user-or-team-select"
              getOptionValue={(val) => (val ? `${val.Name}-${val.Type}` : '')}
            />
          );
        }
        return original.Name;
      },
      meta: { width: '15%', className: 'overflow-hidden' },
    }),
    columnHelper.accessor('Type', {
      header: 'Type',
      cell: ({ getValue }) => {
        const type = getValue<string>();
        return type.charAt(0).toUpperCase() + type.slice(1);
      },
      meta: { width: '15%', className: 'overflow-hidden' },
    }),
    columnHelper.accessor('Role', {
      header: 'Role',
      id: 'role',
      cell: ({ row: { original, index }, table }) => {
        if (!isEditableTableMeta(table.options.meta)) {
          return null;
        }

        const editableRowIndex = table.options.meta.getEditableRow();
        return index === editableRowIndex || original.Id === NEW_ROW_ID ? (
          <RoleCell
            item={original}
            roleOptions={roleOptions}
            onChangeRole={handleRoleChange}
          />
        ) : (
          <div>{original.Role.Name}</div>
        );
      },
      meta: { width: '70%', className: 'overflow-hidden' },
    }),
    actionsColumn<UnifiedAccess>(onRemove),
  ];

  function updateAccess(
    item: UnifiedAccess,
    updater: (prev: UnifiedAccess) => UnifiedAccess
  ) {
    const newAccessList = [...accessList];
    const index = newAccessList.findIndex(
      (access) => access.Type === item.Type && access.PartyId === item.PartyId
    );
    if (index !== -1) {
      newAccessList[index] = updater(newAccessList[index]);
      onChange(newAccessList);
    }
  }

  function handleRoleChange(item: UnifiedAccess, newRoleId: number) {
    const roleName =
      roleOptions.find((r) => r.value === newRoleId)?.label || '';

    updateAccess(item, (prev) => ({
      ...prev,
      Role: { Id: newRoleId, Name: roleName },
    }));
  }

  function handleUsersAndTeamsChange(
    item: UnifiedAccess,
    newTeamOrUser: UserTeamValue | null
  ) {
    updateAccess(item, (prev) => ({
      ...prev,
      PartyId: newTeamOrUser?.PartyId || null,
      Name: newTeamOrUser?.Name || '',
      Type: (newTeamOrUser?.Type || '') as unknown as 'user' | 'team',
    }));
  }
}

function RoleCell({
  item,
  roleOptions,
  onChangeRole,
}: {
  item: UnifiedAccess;
  roleOptions: Option<number>[];
  onChangeRole: (item: UnifiedAccess, newRoleId: number) => void;
}) {
  return (
    <span className="min-w-max">
      <PortainerSelect<number | null>
        value={item.Role.Id}
        onChange={(value) => onChangeRole(item, value || 0)}
        options={roleOptions}
        placeholder="Select a role"
        bindToBody
        data-cy={`role-select-${item.Type}-${item.PartyId}`}
      />
    </span>
  );
}
